import React from 'react';

import { Icon, Stack, Text } from '@eluve/components';
import { getFormattedLocalDateTime } from '@eluve/date-utils';
import {
  useAppointmentId,
  useAppointmentPatient,
  useListenForAppointmentPatient,
  useTenantId,
} from '@eluve/frontend-appointment-hooks';
import { BaseCitation, CitationTag } from '@eluve/frontend-feature-appointment';
import { ViewConversationButton } from '@eluve/frontend-feature-avatar';
import { useTranslation } from '@eluve/i18n';
import {
  Citation,
  PatientOverviewDetail,
  patientOverviewSchema,
} from '@eluve/llm-outputs';

// Adapter: Convert Citation to BaseCitation
const citationToBaseCitation = (
  citation: Citation,
  fallbackTitle: string,
): BaseCitation => ({
  documentTitle: citation.document_title || fallbackTitle,
  citedText: citation.cited_text,
  artifactId: citation.artifact_id,
});

// Component to render text with citation tags
const PatientOverviewCitationText: React.FC<{
  text: string;
  citations?: Citation[];
  tenantId: string | null;
}> = ({ text, citations, tenantId }) => {
  const { t } = useTranslation();
  if (!citations || citations.length === 0) {
    return <span>{text}</span>;
  }

  // Create a mapping of unique sources to avoid duplicate pills
  const seenSources = new Set<string>();
  const uniqueCitations = citations.filter((citation) => {
    const sourceKey = `${citation.document_title}-${citation.document_index}-${citation.artifact_id ?? ''}`;
    if (seenSources.has(sourceKey)) {
      return false;
    }
    seenSources.add(sourceKey);
    return true;
  });

  return (
    <>
      {text}
      {uniqueCitations.map((citation, idx) => (
        <CitationTag
          key={idx}
          citation={citationToBaseCitation(
            citation,
            t('patients.overview.sourceFallback'),
          )}
          tenantId={tenantId}
        />
      ))}
    </>
  );
};

interface PatientOverViewDetailsProps {
  details: PatientOverviewDetail[];
  date: string;
  includeTimestamp?: boolean;
  tenantId: string | null;
}

// Component to render details with citations
const PatientOverviewDetailsWithCitations: React.FC<{
  details: PatientOverviewDetail[];
  tenantId: string | null;
}> = ({ details, tenantId }) => {
  return (
    <Stack gap={3}>
      {details.map((item, idx) => (
        <Text key={idx} color="secondary">
          <PatientOverviewCitationText
            text={item.text}
            citations={item.citations}
            tenantId={tenantId}
          />
        </Text>
      ))}
    </Stack>
  );
};

const PatientOveriewDetails: React.FC<PatientOverViewDetailsProps> = ({
  details,
  date,
  includeTimestamp = true,
  tenantId,
}) => {
  return (
    <Stack gap={2}>
      {includeTimestamp && <Text>{getFormattedLocalDateTime(date)}</Text>}
      <PatientOverviewDetailsWithCitations
        details={details}
        tenantId={tenantId}
      />
    </Stack>
  );
};

// Component to render reason for visit (plain text, no citations or bullet points)
const ReasonForVisitSection: React.FC<{
  reasonForVisit: PatientOverviewDetail[];
}> = ({ reasonForVisit }) => {
  const reasonText = reasonForVisit.map((item) => item.text).join(', ');

  return (
    <Text color="tertiary" size="xs">
      {reasonText}
    </Text>
  );
};

export interface PatientOverviewData {
  reasonForVisit: PatientOverviewDetail[];
  details: PatientOverviewDetail[];
  date: string;
  id: string;
}

export interface PatientOverviewPresentationProps {
  currentOverview: PatientOverviewData;
  allOverviews: PatientOverviewData[];
  includeIcon?: boolean;
  includeHistory?: boolean;
  tenantId: string | null;
  appointmentId?: string;
  showConversationButton?: boolean;
}

export const PatientOverviewPresentation: React.FC<
  PatientOverviewPresentationProps
> = ({
  currentOverview,
  allOverviews,
  includeIcon = false,
  includeHistory = false,
  tenantId,
  appointmentId,
  showConversationButton = true,
}) => {
  const { t } = useTranslation();
  const { reasonForVisit } = currentOverview;
  const hasReasonForVisit = reasonForVisit && reasonForVisit.length > 0;

  return (
    <Stack align="stretch" gap={6} key={currentOverview.id}>
      {hasReasonForVisit && (
        <Stack
          align="start"
          className="rounded-lg bg-bg-tertiary p-3 pr-6"
          gap={3}
          direction="horizontal"
        >
          {includeIcon && (
            <div className="rounded-md bg-bg-insights-strong-primary p-2.5 text-text-insights-strong">
              <Icon name="Lightbulb" size="lg" />
            </div>
          )}
          <Stack className="text-bg-tertiary">
            <Text inline weight="bold" color="secondary" size="md">
              {t('patients.overview.reasonForVisit')}
            </Text>
            <ReasonForVisitSection reasonForVisit={reasonForVisit} />
          </Stack>
          {showConversationButton && appointmentId && (
            <div className="ml-auto">
              <ViewConversationButton appointmentId={appointmentId} />
            </div>
          )}
        </Stack>
      )}

      {(includeHistory ? allOverviews : [allOverviews[0]]).map((overview) => {
        if (!overview) return null;
        return (
          <PatientOveriewDetails
            includeTimestamp={includeHistory}
            details={overview.details}
            date={overview.date}
            key={overview.id}
            tenantId={tenantId}
          />
        );
      })}
    </Stack>
  );
};

PatientOverviewPresentation.displayName = 'PatientOverviewPresentation';

export const PatientOverview: React.FC<{
  includeIcon?: boolean;
  includeHistory?: boolean;
}> = ({ includeIcon = false, includeHistory = false }) => {
  const patient = useAppointmentPatient();
  const appointmentId = useAppointmentId();
  const tenantId = useTenantId();

  useListenForAppointmentPatient();

  const patientOverviews = patient?.patient_overviews ?? [];
  const currentPatientOverview = patientOverviews.find(
    (overview) => overview.isCurrent,
  );
  const parsedCurrentPatientOverview = patientOverviewSchema.safeParse(
    currentPatientOverview?.content ?? '',
  );

  // If parsing fails (legacy format or invalid data), don't display anything
  if (!parsedCurrentPatientOverview.success) {
    return null;
  }

  const allPatientOverviewsDetails: PatientOverviewData[] = patientOverviews
    .map((overview): PatientOverviewData | null => {
      const parsed = patientOverviewSchema.safeParse(overview?.content ?? '');
      // Skip overviews that don't match the new format
      if (!parsed.success) {
        return null;
      }
      const { details, reasonForVisit } = parsed.data;

      return {
        details,
        reasonForVisit,
        date: overview.createdAt,
        id: overview.id,
      };
    })
    .filter((overview): overview is PatientOverviewData => overview !== null);

  if (!allPatientOverviewsDetails.length) {
    return null;
  }

  const currentOverview: PatientOverviewData = {
    reasonForVisit: parsedCurrentPatientOverview.data.reasonForVisit,
    details: allPatientOverviewsDetails[0]?.details ?? [],
    date: currentPatientOverview?.createdAt ?? '',
    id: currentPatientOverview?.id ?? '',
  };

  return (
    <PatientOverviewPresentation
      currentOverview={currentOverview}
      allOverviews={allPatientOverviewsDetails}
      includeIcon={includeIcon}
      includeHistory={includeHistory}
      tenantId={tenantId}
      appointmentId={appointmentId}
    />
  );
};

PatientOverview.displayName = 'PatientOverview';
